import React, { useState, useEffect } from 'react';
import { Box, Button, useTheme, Typography, Select, MenuItem, FormControl, CircularProgress } from '@mui/material';
import { motion } from 'motion/react';
import { useDispatch } from 'react-redux';
import { Step, useStep, setStep } from '../../../state/map';
import { getStepConfig } from '../../../config/steps';
import Stepper from './Stepper';
import { usePriceFormatted, usePriceIsLoading, useCurrency, setCurrency, usePrice } from '../../../state/image';
import { Currency } from '../../../types';
import { useAppDispatch } from '../../../state/types';
import { submitMap, useMapError } from "../../../state/map";

const BottomBar: React.FC = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const [disable, setDisable] = useState(false);
  const appDispatch = useAppDispatch();
  const error = useMapError();
  const currentStep = useStep();
  const priceFormatted = usePriceFormatted();
  const price = usePrice();
  const priceIsLoading = usePriceIsLoading();
  const currency = useCurrency();
  
  const [showExpandedPanel, setShowExpandedPanel] = useState(false);

  const handleStepClick = (step: Step) => {
    dispatch(setStep(step));
    setShowExpandedPanel(true); // Directly open expanded panel
  };

  const handleMinimize = () => {
    setShowExpandedPanel(false);
  };

  const handleClose = () => {
    setShowExpandedPanel(false);
  };

  const onCurrencyChange = (newCurrency: Currency) => {
    appDispatch(setCurrency(newCurrency));
  };

  const currentStepConfig = getStepConfig(currentStep);

  useEffect(() => {
    if (disable) {
      const task = setTimeout(() => setDisable(false), 10_000);
      return () => clearTimeout(task);
    }
  }, [disable, setDisable])

  const handleSubmit = () => {
    setDisable(true);
    dispatch(submitMap());
  };

  if (!currentStepConfig) {
    return null;
  }

  const ExpandedPanelComponent = currentStepConfig.expandedPanel;

  return (
    <>
      {/* Expanded Panel - Renders above everything with higher z-index */}
      <ExpandedPanelComponent
        isActive={showExpandedPanel}
        onMinimize={handleMinimize}
        onClose={handleClose}
      />

      {/* Bottom Bar Container - Lower z-index so panels can appear above */}
      <Box
        component={motion.div}
        initial={{ y: 100 }}
        animate={{ y: 0 }}
        transition={{ type: "spring", stiffness: 300, damping: 30 }}
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          backgroundColor: theme.palette.background.paper,
          borderTop: `1px solid ${theme.palette.divider}`,
          zIndex: 1000, // Lower z-index than expanded panels
          paddingBottom: '12px', // Extra padding for safe area
          boxShadow: theme.shadows[8]
        }}
      >
        {/* Stepper */}
        <Box sx={{ paddingTop: '12px', paddingX: '16px' }}>
          <Stepper
            activeStep={currentStep}
            onStepClick={handleStepClick}
          />
        </Box>

        {/* Price Display with Currency Selector - Under step pills */}
        <Box
          sx={{
            paddingX: '16px',
            paddingTop: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'flex-start', gap: 2, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 80 }}>
            <Select
              value={currency}
              onChange={(e) => onCurrencyChange(e.target.value as Currency)}
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.divider
                },
                '& .MuiSelect-select': {
                  py: 0.5,
                  fontSize: '0.875rem',
                  fontWeight: 500
                }
              }}
            >
              {Object.values(Currency).map((curr) => (
                <MenuItem key={curr} value={curr}>
                  {curr}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          {priceIsLoading ? (
            <CircularProgress size={20} />
          ) : (
            <Typography variant="h6" fontWeight={600} color="primary">
              {price === 0 ? "Free" : priceFormatted || 'Calculating...'}
            </Typography>
          )}
          </Box>
          <Button variant="contained" onClick={() => {dispatch(setStep(Step.Size)); setShowExpandedPanel(true)}} disabled={!!error || disable || currentStep === Step.Size}>
            Finalize
          </Button>
        </Box>
      </Box>
    </>
  );
};

export default BottomBar;
